import { getReportPreferSettingData, changeReportPreferSetting } from "@/api/myPractice";

function filterDisplayItems(data, type) {
    // console.log('filterDisplayItems', data, type)
    return data
      .filter(item => item.display)        // remove all display:false
      .map(item => ({
        id: item.name,
        label: item.displayName,
        type: item.enabled == null ? "default" : "with-toggle",
        is_toggler_on: item.enabled,
        sub_items: item.subcategories 
          ? filterDisplayItems(item.subcategories, type)     // recurse into children
          : undefined,
        original_order: item.order,
    })).sort((a, b) => a.original_order - b.original_order);
}

const reportPreferences = {
    namespaced: true,
    state: {
        loading:false,
        is_report_preferences_fetched_success:null,

        backend_full_report_preferences_setting_data: null,

        ordered_tests: null,

        backend_test_display_order: null,
        default_test_display_order: null,

        // for report styles, content tags
        backend_test_tags: null,
        
        backend_titles_order: null,
        default_titles_order: null,

        backend_custom_content_order: null,
        default_custom_content_order: null,

        // structure of backend_custom_content_setting_section_order and default_custom_content_setting_section_order:
        // {
        //     <report_name>: Array<{title: string, display: boolean}>
        // }
        // NOTES: array is sorted by section order.
        backend_custom_content_setting_section_order: null,
        default_custom_content_setting_section_order: null,
    },
    getters: {
        custome_report_table_data:(state) => () => {
            const data = [];
            state.backend_test_display_order?.forEach(report_name => {
               data.push({
                   id: report_name,
                   report_name,
                   ...state.backend_test_tags[report_name],
               })
            })
            return data;
        },
        backend_test_display_priority_table_data:(state) => () => {
            const data = [];
            state.backend_test_display_order.forEach(report_name => {
               data.push({
                   id: report_name,
                   label: report_name,
                   type: "default"
               })
            })
            return data;
        },
        default_test_display_priority_table_data:(state) => () => {
            const data = [];
            state.default_test_display_order.forEach(report_name => {
               data.push({
                   id: report_name,
                   label: report_name,
                   type: "default"
               })
            })
            return data;
        },
        default_title_order_draggable_list_data:(state) => (report_name) => {
            // console.log('title_order_draggable_list_data', state.default_titles_order[report_name])
            return {
                title: state.default_titles_order[report_name].title,
                sub_items: filterDisplayItems(JSON.parse(JSON.stringify(state.default_titles_order[report_name].subcategories)),'default')
            }
        },
        default_custom_content_draggable_list_data:(state) => (report_name, content_type) => {
            // console.log('custom_content_draggable_list_data', state.current_custom_content_order)
            return {
                title: state.default_custom_content_order[report_name].title,
                // if the user select descriptive, return all displayed customized items
                // if the user select marker only, return only displayed "Markers" items
                sub_items: content_type == "descriptive" ? filterDisplayItems(JSON.parse(JSON.stringify(state.default_custom_content_order[report_name].subcategories)), 'with-toggle') : filterDisplayItems(JSON.parse(JSON.stringify(state.default_custom_content_order[report_name].subcategories.filter(item => item.name === "Markers"))),'with-toggle')
            }
        },
        title_order_draggable_list_data:(state) => (report_name) => {
            // console.log('title_order_draggable_list_data', state.backend_titles_order[report_name])
            return {
                title: state.backend_titles_order[report_name].title,
                draggable: true,
                sub_items: filterDisplayItems(JSON.parse(JSON.stringify(state.backend_titles_order[report_name].subcategories)),'default')
            }
        },
        custom_content_draggable_list_data:(state) => (report_name, content_type) => {
            // console.log('custom_content_draggable_list_data', state.backend_custom_content_order)
            return {
                title: state.backend_custom_content_order[report_name].title,
                draggable: true,
                // if the user select descriptive, return all displayed customized items
                // if the user select marker only, return only displayed "Markers" items
                sub_items: content_type == "descriptive" ? filterDisplayItems(JSON.parse(JSON.stringify(state.backend_custom_content_order[report_name].subcategories)), 'with-toggle') : filterDisplayItems(JSON.parse(JSON.stringify(state.backend_custom_content_order[report_name].subcategories.filter(item => item.name === "Markers"))),'with-toggle')
            }
        },
        custom_content_setting_data:(state, getters) => (report_name, report_content_setting_value) => {
            const custom_content_setting_section_to_getters_map = {
                "Title Order": {
                    getDataFunction: (getters, ...args) => getters.title_order_draggable_list_data(...args),
                    args: [report_name]
                },
                "Custom Content": {
                    getDataFunction: (getters, ...args) => getters.custom_content_draggable_list_data(...args),
                    args: [report_name, report_content_setting_value]
                },
              };

            // NOTES:2025/11/25: since updating order of all current custom content setting sections needs too much manual work for the backend, set the rules that the frontend uses the default values to decide the sections' order under Custom Content Setting
            const data = state.default_custom_content_setting_section_order[report_name]
            .filter(section => section.display && custom_content_setting_section_to_getters_map[section.title])
            .map(section => {
                return custom_content_setting_section_to_getters_map[section.title].getDataFunction(getters, ...custom_content_setting_section_to_getters_map[section.title].args);
            });
            return data;
        },
        default_custom_content_setting_data:(state, getters) => (report_name, report_content_setting_value) => {
            const custom_content_setting_section_to_getters_map = {
                "Title Order": {
                    getDataFunction: (getters, ...args) => getters.default_title_order_draggable_list_data(...args),
                    args: [report_name]
                },
                "Custom Content": {
                    getDataFunction: (getters, ...args) => getters.default_custom_content_draggable_list_data(...args),
                    args: [report_name, report_content_setting_value]
                },
              };

            // NOTES:2025/11/25: since updating order of all current custom content setting sections needs too much manual work for the backend, set the rules that the frontend uses the default values to decide the sections' order under Custom Content Setting
            const data = state.default_custom_content_setting_section_order[report_name]
            .filter(section => section.display && custom_content_setting_section_to_getters_map[section.title])
            .map(section => {
                return custom_content_setting_section_to_getters_map[section.title].getDataFunction(getters, ...custom_content_setting_section_to_getters_map[section.title].args);
            });
            return data;
        },
        merged_multiple_custom_content_setting_data:(state, getters) => (report_group, report_content_setting_value) => {
            const custom_content_setting_section_to_getters_function_map = {
                "Title Order": (getters, ...args) => getters.title_order_draggable_list_data(...args),
                "Custom Content": (getters, ...args) => getters.custom_content_draggable_list_data(...args),
            };

            const custom_content_setting_section_to_getters_args_map = {};
            report_group.forEach(report_name => {
                custom_content_setting_section_to_getters_args_map[report_name] = {
                    "Title Order": {
                        args: [report_name]
                    },
                    "Custom Content": {
                        args: [report_name, report_content_setting_value]
                    },
                };
            });

            // for group setting, use the first report's custom content setting section order
            // NOTES: all reports' custom content setting section order and display status should be the same.
            const custom_content_setting_section_order = state.default_custom_content_setting_section_order[report_group[0]];
            
            // merge all reports' corresponding sections' data of custom content setting
            const custom_content_setting_data_of_report_group = custom_content_setting_section_order
            .filter(section => section.display && custom_content_setting_section_to_getters_function_map[section.title])
            .map(section => {
                const all_reports_custom_content_setting_section_data = report_group.map(report_name => {
                    return custom_content_setting_section_to_getters_function_map[section.title](getters, ...custom_content_setting_section_to_getters_args_map[report_name][section.title].args);
                });
                const merged_custom_content_setting_section_data = all_reports_custom_content_setting_section_data[0];
                merged_custom_content_setting_section_data.sub_items = mergeMultipleSectionalCustomContentSettingData(all_reports_custom_content_setting_section_data.map(data => data.sub_items), report_group);
                return merged_custom_content_setting_section_data;
            });

            return custom_content_setting_data_of_report_group;
            
        },
        target_edit_report_data:(state, getters) => (target_reports, is_group_setting = false) => {
        // used as the initial value for ReportPreferencesSettingDialog
            // @param target_reports: an array of report names
            // @param is_group_setting: boolean, default is false
            if(is_group_setting){
                // Title Order: merge all target edit reports' title order data
                const all_target_edit_reports_title_order_data = target_reports.map(single_report_name => {
                    return getters.title_order_draggable_list_data(single_report_name);
                });
                const merged_title_order_data = all_target_edit_reports_title_order_data[0];
                merged_title_order_data.sub_items = mergeMultipleSectionalCustomContentSettingData(all_target_edit_reports_title_order_data.map(data => data.sub_items), target_reports);
                // Custom Content: merge all target edit reports' custom content data
                // NOTES: for group setting, the default initial content type value is unified to "markerOnly"
                const all_target_edit_reports_custom_content_data = target_reports.map(single_report_name => {
                    return getters.custom_content_draggable_list_data(single_report_name, "markerOnly");
                });
                const merged_custom_content_data = all_target_edit_reports_custom_content_data[0];
                merged_custom_content_data.sub_items = mergeMultipleSectionalCustomContentSettingData(all_target_edit_reports_custom_content_data.map(data => data.sub_items), target_reports);
                

                return {
                    // NOTES: for group setting, the default initial style value is unified to "classic"
                    "Choose your style": 'advanced',
                    // NOTES: for group setting, the default initial content type value is unified to "markerOnly"
                    "Report Content Setting": ["markerOnly"],
                    "Custom Content Setting": [
                        merged_title_order_data,
                        merged_custom_content_data,
                    ]
                }; 
            }else{
                // is_group_setting is false: not call from group setting entrance
                // single report:
                const single_report_name = target_reports[0];
                return {
                    "Choose your style": state.backend_test_tags[single_report_name].style,
                    "Report Content Setting": state.backend_test_tags[single_report_name].content,
                    "Custom Content Setting": [
                        getters.title_order_draggable_list_data(single_report_name),
                        getters.custom_content_draggable_list_data(single_report_name, state.backend_test_tags[single_report_name].content.includes("descriptive") ? "descriptive" : "markerOnly"),
                    ]
                };
            }
        },
        backend_test_tags:(state) => (report_name) => {
            return state.backend_test_tags[report_name];
        }
    },
    mutations: {
        SET_LOADING(state, data) {
            state.loading = data
        },
        SET_IS_REPORT_PREFERENCES_FETCH_SUCCESS(state, data) {
            state.is_report_preferences_fetched_success = data
        },
        SET_BACKEND_FULL_REPORT_PREFERENCES_SETTING_DATA(state, data) {
            state.backend_full_report_preferences_setting_data = data
        },
        SET_ORDERED_TESTS_DATA(state, data) {
            state.ordered_tests = data
        },
        SET_BACKEND_TEST_DISPLAY_ORDER_DATA(state, data) {
            state.backend_test_display_order = data
        },
        SET_DEFAULT_TEST_DISPLAY_ORDER_DATA(state, data) {
            state.default_test_display_order = data
        },
        SET_BACKEND_TEST_TAGS_DATA(state, data) {
            state.backend_test_tags = data
        },
        SET_BACKEND_TITLES_ORDER_DATA(state, data) {
            state.backend_titles_order = data
        },
        SET_DEFAULT_TITLES_ORDER_DATA(state, data) {
            state.default_titles_order = data
        },
        SET_BACKEND_CUSTOM_CONTENT_ORDER_DATA(state, data) {
            state.backend_custom_content_order = data
        },
        SET_DEFAULT_CUSTOM_CONTENT_ORDER_DATA(state, data) {
            state.default_custom_content_order = data
        },
        SET_BACKEND_CUSTOM_CONTENT_SETTING_SECTION_ORDER_DATA(state, data) {
            state.backend_custom_content_setting_section_order = data
        },
        SET_DEFAULT_CUSTOM_CONTENT_SETTING_SECTION_ORDER_DATA(state, data) {
            state.default_custom_content_setting_section_order = data
        },
    },
    actions: {
        async fetchReportPreferencesDataFromBackend({ state, commit, dispatch }) {
            // 1. call api to fetch the data from backend
            console.log("fetching report preferences data from backend ...");
            try{
                commit("SET_LOADING", true);
                // const response = {
                //     data:mockData
                // };
                // Check if customer_id is 999997 to determine which reports to fetch
                const userInfo = JSON.parse(sessionStorage.getItem('userInfo') || '{}');
              
                const reportTypes = userInfo && userInfo.customer_id === 999997 
                    ? '["cardio_genetics","cardio_zoomer","gut_zoomer_4"]'
                    : '["cardio_genetics","cardio_zoomer"]';
                const response = await getReportPreferSettingData(`setting/getSpecificReportPreferSettings?reportTypes=${reportTypes}`);
                // console.log(response.data)
                // 2. initiate all necessary states
                await dispatch("initiateReportPreferencesDataViaBackendResponse", response.data);
                commit("SET_IS_REPORT_PREFERENCES_FETCH_SUCCESS", true);
            }catch(error){
                console.log("fetchReportPreferencesDataFromBackend error", error);
                commit("SET_IS_REPORT_PREFERENCES_FETCH_SUCCESS", false);
            }finally{
                commit("SET_LOADING", false);
            }
        },
        initiateReportPreferencesDataViaBackendResponse({ state, commit }, raw_data) {
            // 1. store full data.
            commit("SET_BACKEND_FULL_REPORT_PREFERENCES_SETTING_DATA", raw_data);
            // 2. get current and default test priority display order
            commit("SET_BACKEND_TEST_DISPLAY_ORDER_DATA", raw_data["test_display_order"])
            commit("SET_DEFAULT_TEST_DISPLAY_ORDER_DATA", raw_data["test_display_order_default"]);

            commit("SET_ORDERED_TESTS_DATA", JSON.parse(JSON.stringify(raw_data["test_display_order"])))
            

            // 3. get tests details info, current and default
            // NOTES: test_tags only need to record the backend value;
            // NOTES: all of the above should be null or object with reportName as key
            // NOTES: current is the current value before saved. backend is the value from backend. default is the default value from backend
            const backend_titles_order = {};
            const default_titles_order = {};
            const backend_custom_content_order = {};
            const default_custom_content_order = {};
            const backend_custom_content_setting_section_order = {};
            const default_custom_content_setting_section_order = {};
            const backend_test_tags = {};
            state.backend_test_display_order.forEach(report_name => {
                const current_preference_key = report_name.toLowerCase().replaceAll(" ", "_") + "_info";
                const default_preference_key = report_name.toLowerCase().replaceAll(" ", "_") + "_info_default";

                if (!raw_data[current_preference_key] || !raw_data[default_preference_key]) {
                    console.warn(`Report preferences: missing data for "${report_name}" (key: ${current_preference_key})`);
                    return;
                }

                backend_test_tags[report_name] = {
                    style: raw_data[current_preference_key]["reportStyle"],
                    content:[raw_data[current_preference_key]["reportContentSetting"]],
                }
                if(!raw_data[current_preference_key]["customContentSetting"].find(item => item.title === "Reset to default settings").enabled){
                    backend_test_tags[report_name].content.push('customComments');
                }

                backend_titles_order[report_name] = JSON.parse(JSON.stringify(raw_data[current_preference_key]["customContentSetting"].find(item => item.title === "Title Order")));
                default_titles_order[report_name] = JSON.parse(JSON.stringify(raw_data[default_preference_key]["customContentSetting"].find(item => item.title === "Title Order")));

                backend_custom_content_order[report_name] = JSON.parse(JSON.stringify(raw_data[current_preference_key]["customContentSetting"].find(item => item.title === "Custom Content")));
                default_custom_content_order[report_name] = JSON.parse(JSON.stringify(raw_data[default_preference_key]["customContentSetting"].find(item => item.title === "Custom Content")));

                backend_custom_content_setting_section_order[report_name] = raw_data[current_preference_key]["customContentSetting"].sort((a, b) => a.order - b.order).map(item => {return {title:item.title, display: item.display}});
                default_custom_content_setting_section_order[report_name] = raw_data[default_preference_key]["customContentSetting"].sort((a, b) => a.order - b.order).map(item => {return {title:item.title, display: item.display}});
            })
            commit("SET_BACKEND_TEST_TAGS_DATA", backend_test_tags);
            commit("SET_BACKEND_TITLES_ORDER_DATA", backend_titles_order);
            commit("SET_DEFAULT_TITLES_ORDER_DATA", default_titles_order);
            commit("SET_BACKEND_CUSTOM_CONTENT_ORDER_DATA", backend_custom_content_order);
            commit("SET_DEFAULT_CUSTOM_CONTENT_ORDER_DATA", default_custom_content_order);
            commit("SET_BACKEND_CUSTOM_CONTENT_SETTING_SECTION_ORDER_DATA", backend_custom_content_setting_section_order);
            commit("SET_DEFAULT_CUSTOM_CONTENT_SETTING_SECTION_ORDER_DATA", default_custom_content_setting_section_order);
        },
        buildCurrentCustomContentSettingFieldViaCurrentReportPreferencesSettingDialogValues({ state, commit }, payload) {
            const {current_report_preferences_setting_dialog_values, report_name} = payload;
            // console.log("current_report_preferences_setting_dialog_values", current_report_preferences_setting_dialog_values);

            // 1. get original customContentSetting value
            const restored_customContentSetting_value = JSON.parse(JSON.stringify(state.backend_full_report_preferences_setting_data[report_name.toLowerCase().replaceAll(" ", "_") + "_info"].customContentSetting));
            // 2. update object with title 'Reset to default settings':
            //  NOTES: if "Report Content Settings" in current_report_preferences_setting_dialog_values doesn't have "customComments", set enabled to true
            restored_customContentSetting_value.find(item => item.title === "Reset to default settings").enabled = !current_report_preferences_setting_dialog_values['Report Content Setting'].includes('customComments');
            
            // 3. process title order's current order number based on initial value
            const current_title_order_map = processNewOrderData(current_report_preferences_setting_dialog_values['Custom Content Setting'].filter(item => item.title === "Title Order")[0].sub_items, report_name);
            // 4. update object with title "Title Order"
            updateOrderInCustomContentSettingValue(restored_customContentSetting_value.find(item => item.title === "Title Order").subcategories, current_title_order_map);
            // console.log("current_title_order_map", restored_customContentSetting_value.find(item => item.title === "Title Order"));
            
            // 5. process custom content's current order number based on initial value
            const current_custom_content_map = processNewOrderData(current_report_preferences_setting_dialog_values['Custom Content Setting'].filter(item => item.title === "Custom Content")[0].sub_items, report_name);
            // 6. update object with title "Custom Content"
            updateOrderInCustomContentSettingValue(restored_customContentSetting_value.find(item => item.title === "Custom Content").subcategories, current_custom_content_map);
            // console.log("current_custom_content_map", restored_customContentSetting_value.find(item => item.title === "Custom Content"));

            return restored_customContentSetting_value;
        },
        async saveReportPreferencesDataInBackend({ state, dispatch }, payload) {
            // NOTES: payload is full data structure compared with the one fetched from backend
            // 1.initiate params with test_display_order and old_test_display_order
            const params = {
                test_display_order: payload.test_display_order,
                old_test_display_order: state.backend_test_display_order,
            };
            // 2. process each report's details
            payload['test_display_order'].forEach(report_name => {
                const key = report_name.toLowerCase().replaceAll(" ", "_") + "_info";
                params[key] = payload[key];
                params[`old_${key}`] = state.backend_full_report_preferences_setting_data[key];
            })
            // 3. call api to save the params to backend
            // console.log("calling backend to save report preferences data ...", params);
            try{
                const response = await changeReportPreferSetting('setting/setReportPreferCustomerV2', params);
                // 4. IF save successfully, update all states with new values
                dispatch("initiateReportPreferencesDataViaBackendResponse", payload);
                console.log("saveReportPreferencesDataInBackend", payload);
                return true;
            }catch(err){
                console.log("saveReportPreferencesDataInBackend error", err);
                return false;
            }
        },
        saveReportPreferencesDataToLocalBackend({ state, dispatch }, payload) {
            const params = {
                test_display_order: String(payload.test_display_order),
                old_test_display_order: String(state.backend_test_display_order),
            };
            // 2. process each report's details
            payload['test_display_order'].forEach(report_name => {
                const key = report_name.toLowerCase().replaceAll(" ", "_") + "_info";
                params[key] = JSON.stringify(payload[key]);
                params[`old_${key}`] = JSON.stringify(state.backend_full_report_preferences_setting_data[key]);
            })
            try{
                dispatch("initiateReportPreferencesDataViaBackendResponse", payload);
                return true;
            }catch(err){
                console.log("saveReportPreferencesDataInBackend error", err);
                return false;
            }
        },
        initiateReportPreferencesDataForReportTags({ commit }, raw_data) {
            const backend_test_tags = {};
            Object.keys(raw_data).forEach(report_name => {
                backend_test_tags[report_name] = {
                    style: raw_data[report_name]["reportStyle"],
                    content:[raw_data[report_name]["reportContentSetting"]],
                }
                if(!raw_data[report_name]["customContentSetting"].find(item => item.title === "Reset to default settings").enabled){
                    backend_test_tags[report_name].content.push('customComments');
                }
            })
            commit("SET_BACKEND_TEST_TAGS_DATA", backend_test_tags);
        },
    },
}

// Helper function to recursively merge sub_items at any nesting level
function mergeSubItemsRecursively(existing_sub_items, new_sub_items, report_name) {
    new_sub_items.forEach(new_sub_item => {
        const existing_sub_item = existing_sub_items.find(si => si.id === new_sub_item.id);
        if (existing_sub_item) {
            // Merge original_order for this sub_item
            if (!existing_sub_item.original_order) {
                existing_sub_item.original_order = {};
            }
            existing_sub_item.original_order[report_name] = new_sub_item.original_order;
            
            // Recursively handle nested sub_items if they exist
            if (new_sub_item.sub_items && new_sub_item.sub_items.length > 0) {
                if (!existing_sub_item.sub_items) {
                    existing_sub_item.sub_items = [];
                }
                // Recursive call for arbitrary nesting depth
                mergeSubItemsRecursively(existing_sub_item.sub_items, new_sub_item.sub_items, report_name);
            }
        } else {
            // New sub_item, add it with proper original_order structure
            const sub_item_copy = {...new_sub_item};
            sub_item_copy.original_order = {[report_name]: new_sub_item.original_order};
            // Recursively process nested sub_items if they exist
            if (sub_item_copy.sub_items && sub_item_copy.sub_items.length > 0) {
                sub_item_copy.sub_items = mergeMultipleSectionalCustomContentSettingData(
                    [sub_item_copy.sub_items], 
                    [report_name]
                );
            }
            existing_sub_items.push(sub_item_copy);
        }
    });
}

function mergeMultipleSectionalCustomContentSettingData(multiple_items_map, report_group){
    // @param multiple_items_map: Array of items arrays from different reports
    // @param report_group: Array of report names corresponding to each items array
    // Recursively merge items by id, including nested sub_items at all levels
    const items_map = new Map();
    multiple_items_map.forEach((items_array, array_index) => {
        const report_name = report_group[array_index];
        items_array.forEach((item) => {
            if (!items_map.has(item.id)) {
                // First occurrence: parse the item and add it to the map
                const item_copy = {...item};
                item_copy['original_order'] = {[report_name]: item.original_order};
                if(item.type === "with-toggle"){
                    item_copy['is_toggler_on'] = true;
                }
                // Recursively process sub_items if they exist
                if (item_copy.sub_items && item_copy.sub_items.length > 0) {
                    item_copy.sub_items = mergeMultipleSectionalCustomContentSettingData(
                        [item_copy.sub_items], 
                        [report_name]
                    );
                }
                items_map.set(item.id, item_copy);
            } else {
                // Item already exists: merge original_order and sub_items if present
                const existing_item = items_map.get(item.id);
                // Merge the original_order instead of overwriting
                existing_item['original_order'][report_name] = item.original_order;
                if (item.sub_items && item.sub_items.length > 0) {
                    if (!existing_item.sub_items) {
                        existing_item.sub_items = [];
                    }
                    // Use recursive helper to merge sub_items at any nesting level
                    mergeSubItemsRecursively(existing_item.sub_items, item.sub_items, report_name);
                }
            }
        });
    });
    
    return Array.from(items_map.values());
}

function processNewOrderData(items, report_name){
    // build a map of current items {<report_name>: <...items, sub_items(Object)>}
    const current_items_map = {};
    const current_level_original_orders = items.map(item=>{
        // if group setting: original_order is an object {<report_name>: <original_order>}
        if(typeof item.original_order === "object"){
            return item.original_order[report_name];
        }else{
            return item.original_order;
        }
    }).filter(order => order != null).sort((a,b) => a - b);
    items.filter(item => {
        if(typeof item.original_order === "object"){
            return item.original_order[report_name] != null;
        }else{
            return item.original_order != null;
        }
    }).forEach((item, index)=>{
        current_items_map[item.label] = {
            ...item,
            current_order: current_level_original_orders[index],
            // used to quickly find the corresponding sub-item.
            sub_items_map: item.sub_items ? processNewOrderData(item.sub_items, report_name) : undefined
        }
    })
    return current_items_map;
}
// 

function updateOrderInCustomContentSettingValue(initial_values, current_items_map){
    initial_values.forEach(item=>{
        if(item.display){
            const row_name = item.displayName;
            if(current_items_map[row_name]){
                item.order = current_items_map[row_name].current_order;
                if(current_items_map[row_name].type == "with-toggle"){
                    item.enabled = current_items_map[row_name].is_toggler_on;
                }
                if(item.subcategories){
                    updateOrderInCustomContentSettingValue(item.subcategories, current_items_map[row_name].sub_items_map);
                }
            }
        }
    });
}

export default reportPreferences;